<template>

    <!-- 飞行进度条 -->
    <flight-progress-bar
    v-if="showFlightProgress"
      ref="flightProgressRef"
      :isShowRSF="showFlightProgress"
      :current-time-reset="currentTime"
      :total-time-reset="totalTime"
      :progress-rate="progressRate"
      :time-info="timeInfo"
      @progress-change="handleProgressChange"
      @update-time-multiple="handleUpdateTimeMultiple"
      @end-flight="handleEndFlight"
      @video-play-status="handleVideoPlayStatus"
    />

    <!-- 姿态球 -->
    <posture-ball
    v-if="showFlightProgress"
      :curSpeed="currentDrone.curSpeed"
      :curPitch="currentDrone.curPitch"
      :curDroneHeading="currentDrone.curDroneHeading"
      :curGimbalHeading="currentDrone.curGimbalHeading"
      :curHeightASL="currentDrone.heightASL"
      :curHeightALT="currentDrone.heightALT"
      :curHeightAGL="currentDrone.heightAGL"
    />

</template>

<script setup>
import { ref, reactive, computed, onMounted, onUnmounted, watch, nextTick } from 'vue'
import { message } from 'ant-design-vue'
import FlightProgressBar from './flightProgressBar.vue'
import PostureBall from './dir-contrainer.vue'
import { useMyStore } from '/@/store'
import emitter from '/@/event-bus'
import { convertToOrientationQuaternion, setFrustumObj, getPointPosition, getDistance, getTimeSecond, getRouterTime, setPolyline, setPointEntity, initializeFlightTimeline } from '/@/utils/kmlParse'
import { viewer } from '/@/hooks/Cesium/use-cesium-map'
import * as Cesium from 'cesium'
import { ZOOM_SCALE_POINT } from '/@/types/device'
import { WaylineActionType } from '/@/types/wayline'
import dayjs from 'dayjs'

const tool = ref()
// 状态管理
const routeStore = useMyStore()
const heightCalculator = ref(null)
// Refs
const flightProgressRef = ref(null)
const isRouteSimulationFlight = ref(false)
const currentTime = ref(0)
const totalTime = ref(0)
const distance = ref(0)
const progressRate = ref(0)
const timeInfo = reactive({
  defaultZoom: 1,
  startTime: 0,
  endTime: 0,
  totalTime: 0,
  waypointInfo: []
})
const currentDrone = ref({
  curDroneHeading: 0,
  curGimbalHeading: 0,
  curPitch: 0,
  curSpeed: 0,
  heightAGL: 0,
  heightALT: 0,
  heightASL: 0,
})

// 显示控制
const showFlightProgress = computed(() => routeStore.state.FlightSimulationFlag)
const showPostureBall = ref(false)

const entityObj = reactive({
  entityIds: [],
  frustum: null
})
const model_flight_path_id = 'model_flight_path'
const model_flight_id = 'model_flight'
// 计算时间信息
const calculateTimeInfo = () => {
  let distance_len = 0
  const timeObj = {
    hoverTime: 0,
    panoShotTimes: 0,
    startRecordTimes: 0,
    zoomTimes: 0,
    psdkTimes: 0,
    rotateYawTimes: 0,
    gimbalRotateYawTimes: 0,
    gimbalRotatePitchTimes: 0,
    takePhotoTimes: 0
  }
  let length = 0
  routeStore.state.editRouteList.forEach((item, index) => {
    item.eventList.forEach(row => {
      if (row.type === 'hover') { timeObj.hoverTime += row.hoverTime * 1 }
      if (row.type === WaylineActionType.PANOSHOT) { timeObj.panoShotTimes += 50 }
      if (row.type === WaylineActionType.TAKE_PHOTO) { timeObj.takePhotoTimes += 1 }
      if (row.type === WaylineActionType.ROTATE_YAW) { timeObj.rotateYawTimes += 2 }
      if (row.type === WaylineActionType.GIMBAL_ROTATE_YAW) { timeObj.gimbalRotateYawTimes += 2 }
      if (row.type === WaylineActionType.GIMBAL_ROTATE_PITCH) { timeObj.gimbalRotatePitchTimes += 1 }
      if (row.type === WaylineActionType.START_RECORD) { timeObj.startRecordTimes += 1 }
      if (row.type === WaylineActionType.ZOOM && row.focalLength !== length) {
        timeObj.zoomTimes += 3
        length = row.focalLength
      }
      if (row.type === WaylineActionType.MEGAPHONE || row.type === WaylineActionType.MEGAPHONE_STOP || row.type === WaylineActionType.SEARCH_LIGHT || row.type === WaylineActionType.SEARCH_LIGHT_STOP) { timeObj.psdkTimes += 1 }
    })
    index !== routeStore.state.editRouteList.length - 1 && (distance_len += Cesium.Cartesian3.distance(item.position, routeStore.state.editRouteList[index + 1].position))
  }
  )
  distance.value = distance_len.toFixed(1)
  const H = getRouterTime(routeStore.state.editRouteList, timeObj, routeStore.state.editRouteList.length)
  const second = H.timeSec
  const time = dayjs('2025-01-01 12:00:00')
  timeInfo.defaultZoom = ZOOM_SCALE_POINT[routeStore.state.editInfo.drone_type][0].defaultZoom
  timeInfo.totalTime = second
  timeInfo.startTime = time.valueOf()
  timeInfo.endTime = time.add(second, 'second').valueOf()
  timeInfo.waypointInfo = []
  totalTime.value = second
  const arr = []
  routeStore.state.editRouteList.forEach((row, index) => {
    const id = `flight_waypoint${index + 1}`
    setPointEntity(viewer, row.position, id)
    entityObj.entityIds.push(id)
    arr.push(row.position)
    calculateWaypointInfo(index)
  }
  )
  const firstPoint = routeStore.state.editRouteList[0].position
  const endPoint = routeStore.state.routeStartPosition ?? firstPoint
  const options = getPointPosition(endPoint, firstPoint)
  const heading = Math.floor(Cesium.Math.toDegrees(options.h) + 90)
  const doingId = 'flight_wayline_doing'
  setPolyline(viewer, {
    id: doingId,
    positions: arr
  })
  entityObj.entityIds.push(doingId)
  heightCalculator.value = initializeFlightTimeline(viewer, timeInfo, {
    multiplier: 1,
    pathId: model_flight_path_id,
    modelId: model_flight_id,
    startPosHeading: heading
  })
  entityObj.entityIds.push(model_flight_path_id, model_flight_id)
  const position = timeInfo.waypointInfo[0].position
  // eslint-disable-next-line no-unused-expressions
  entityObj.frustum = setFrustumObj(viewer, position, {
    heading: heading,
    pitch: 0,
    roll: 0,
    fov: 20
  })
  entityObj.frustum.add()
}

// 飞行进度条事件处理
const handleProgressChange = (progress) => {
  progressRate.value = progress
  currentTime.value = progress * timeInfo.totalTime
  const clickTime = Cesium.JulianDate.fromDate(new Date(timeInfo.startTime))
  const nowTime = Cesium.JulianDate.addSeconds(clickTime, currentTime.value, new Cesium.JulianDate())
  viewer.clock.currentTime = nowTime
  setNowFrustum(nowTime)
}
function calculateWaypointInfo (index) {
  if (!routeStore.state.editRouteList || index >= routeStore.state.editRouteList.length) {
    return
  }

  const point = routeStore.state.editRouteList[index]
  const eventList = point.eventList
  const obj = {
    waypointIndex: index,
    startTime: 0,
    actionTimeInterval: 0,
    otherTimeInterval: 0,
    actionTimes: [],
    waypointSpeed: point.waypointSpeed,
    position: point.position,
    hpr: {
      heading: point.hpr.heading,
      pitch: 90,
      roll: 0
    }
  }

  if (index !== 0) {
    const prevPoint = routeStore.state.editRouteList[index - 1]
    const prevTimeInfo = timeInfo.waypointInfo[index - 1]
    const distance = getDistance(prevPoint.position, point.position)
    const travelTime = Number((distance / prevPoint.waypointSpeed).toFixed(0)) + prevTimeInfo.actionTimeInterval + prevTimeInfo.otherTimeInterval
    obj.startTime = dayjs(prevTimeInfo.startTime).add(travelTime, 'second').valueOf()
  } else {
    obj.startTime = timeInfo.startTime
  }

  let headingInfo
  if (index === routeStore.state.editRouteList.length - 1) {
    const currentPos = routeStore.state.editRouteList[index].position
    const prevPos = routeStore.state.editRouteList[index - 1].position
    headingInfo = getPointPosition(prevPos, currentPos)
  } else {
    const currentPos = routeStore.state.editRouteList[index].position
    const nextPos = routeStore.state.editRouteList[index + 1].position
    headingInfo = getPointPosition(currentPos, nextPos)
  }
  obj.hpr.heading = Math.floor(Cesium.Math.toDegrees(headingInfo.h) + 90)

  let currentLens = 0
  if (eventList && eventList.length > 0) {
    eventList.forEach(event => {
      const eventDuration = getTimeSecond(event.type, event)
      obj.actionTimeInterval += eventDuration
      const actionRow = {
        type: event.type,
        endTime: 0
      }
      switch (event.type) {
        case WaylineActionType.ROTATE_YAW:
          actionRow.heading = Number(event.aircraftHeading)
          break
        case WaylineActionType.GIMBAL_ROTATE_YAW:
          actionRow.heading = Number(event.gimbalYawRotateAngle)
          break
        case WaylineActionType.GIMBAL_ROTATE_PITCH:
          actionRow.gimbalPitchRotateAngle = Number(event.gimbalPitchRotateAngle)
          break
        case WaylineActionType.ZOOM:
          actionRow.focalLength = Number(event.focalLength)
          currentLens === event.focalLength ? obj.actionTimeInterval -= eventDuration : currentLens = event.focalLength
          break
      }
      actionRow.endTime = dayjs(obj.startTime).add(obj.actionTimeInterval, 'second').valueOf()
      obj.actionTimes.push(actionRow)
    })
  }

  if (index !== 0) {
    obj.otherTimeInterval += 4
  }
  timeInfo.waypointInfo.push(obj)
}

const handleUpdateTimeMultiple = (multiple) => {
  viewer.clock.multiplier = multiple
}

const handleEndFlight = () => {
  handleVideoPlayStatus('pause')
  stopSimulationFlight()
  message.success('模拟飞行已结束')
}

const handleVideoPlayStatus = (status) => {
  if (status === 'resume') {
    viewer.clock.shouldAnimate = true
    addTime()
  } else if (status === 'pause') {
    viewer.clock.shouldAnimate = false
    removeTime()
  }
}
let timer = null
function addTime () {
  viewer.clock.onTick.addEventListener(timeChange)
}
function removeTime () {
  viewer.clock.onTick.removeEventListener(timeChange)
}
function timeChange (current) {
  if (timer) return
  const times = Cesium.JulianDate.toDate(current.currentTime)
  currentTime.value = dayjs(times).diff(timeInfo.startTime, 'second')
  progressRate.value = currentTime.value / timeInfo.totalTime
  const current_time = current.currentTime
  setNowFrustum(current_time)
  timer = setTimeout(() => {
    timer = null
  }
  , 20)
}
function getTimeNow (sconed) {
  let row = 0
  const nowTime = Cesium.JulianDate.toDate(sconed)
  const timeValue = dayjs(nowTime).valueOf()
  const info = timeInfo.waypointInfo
  let index = 0
  for (let i = 0; i < info.length && !(info[i].startTime > timeValue); i++) {
    index = i
  }
  row = info[index].waypointSpeed
  return row
}
function setNowFrustum (times) {
  const currentEntity = viewer.entities.getById(model_flight_id)
  if (currentEntity) {
    const currentPosition = currentEntity.position.getValue(times)
    const modelHeading = heightCalculator.value?.modelHeadingProperty?.getValue(times)
    const orientation = convertToOrientationQuaternion(currentPosition, modelHeading)
    currentEntity.orientation = orientation
    const frustumHeading = heightCalculator.value?.frustumHeadingProperty?.getValue(times)
    const frustumPitch = heightCalculator.value?.frustumPitchProperty?.getValue(times)
    entityObj.frustum?.updateFrustum(currentPosition, {
      heading: frustumHeading,
      pitch: frustumPitch
    })
    emitter.emit('setCameraMapView', {
      position: currentPosition,
      heading: (frustumHeading ?? 0) - 90,
      pitch: frustumPitch
    })
    const mapZoom = heightCalculator.value?.mapZoomProperty?.getValue(times)
    const zoomValue = mapZoom ? Number(mapZoom.toFixed(1)) : 0
    const fovValue = getFov(zoomValue)
    entityObj.frustum?.updateFov(fovValue)
    emitter.emit('setFocalLengthOfRouteFlight', zoomValue)
    currentDrone.value.curPitch = Number((frustumPitch ?? currentDrone.value.curPitch).toFixed(1))
    currentDrone.value.curGimbalHeading = frustumHeading ?? 0
    currentDrone.value.curDroneHeading = Number(((modelHeading ?? 0) - 90).toFixed(1))
    currentDrone.value.curSpeed = getTimeNow(times)
    const cartographicPosition = Cesium.Cartographic.fromCartesian(currentPosition)
    currentDrone.value.heightASL = Number((cartographicPosition.height - 0.1).toFixed(1))
    const startCartographic = Cesium.Cartographic.fromCartesian(routeStore.state.routeStartPosition)
    currentDrone.value.heightALT = Number((currentDrone.value.heightASL - startCartographic.height).toFixed(1))
    const longitude = Cesium.Math.toDegrees(cartographicPosition.longitude)
    const latitude = Cesium.Math.toDegrees(cartographicPosition.latitude)
    const terrainHeight = viewer.scene.globe.getHeight(Cesium.Cartographic.fromDegrees(longitude, latitude, 0))
    currentDrone.value.heightAGL = Number((currentDrone.value.heightASL - terrainHeight).toFixed(1))
  }
}

function getFov (t) {
  const i = 2 * Math.atan(0.5333333333333333) * (180 / Math.PI)
  return 1 / (t / i)
}

// 停止模拟飞行
const stopSimulationFlight = () => {
  showPostureBall.value = false
  if (timer) {
    clearInterval(timer)
    timer = null
  }
  if (entityObj.frustum) {
    entityObj.frustum.destroy()
    entityObj.frustum = null
  }
  if (entityObj.entityIds && entityObj.entityIds.length > 0) {
    entityObj.entityIds.forEach(el => {
      viewer.entities.removeById(el)
    })
    entityObj.entityIds = []
  }
  emitter.emit('flyOffline')
}

onMounted(() => {
  calculateTimeInfo()
  nextTick(() => {
    flightProgressRef.value?.updateWaypointMarkers()
    handleProgressChange(0)
  })
})

// 组件销毁
onUnmounted(() => {
  stopSimulationFlight()
})
</script>

<style scoped>
.main-container {
  position: relative;
  width: 100%;
  overflow: hidden;
  z-index: 1500;
}
</style>
